import { Box, Typography, Tabs, Tab, IconButton, Chip } from "@mui/material";
import {
  Upload as UploadIcon,
  Close as CloseIcon,
  PhotoLibrary as GalleryIcon,
  Campaign as MetaAdLibraryIcon,
} from "@mui/icons-material";
import { useMemo, useState } from "react";
import { useTranslationApp } from "../../../lib/i18next";
import UploadFileButton from "../../Buttons/UploadFileButton";
import Button from "../../Buttons/Button";
import SegmentedControl from "../../SegmentedControl/SegmentedControl";
import { useDispatchApp } from "../../../lib/redux";
import { getPathDatabaseByRole } from "../../../actions/auth";
import {
  FONT_SIZE_BUTTON,
  getReferenceAdStoragePath,
  IMAGE_FORMATS,
} from "../../../utils/constants";
import {
  MODAL_CREATE_WITH_IA_REFERENCE_AD_TITLE,
  MODAL_CREATE_WITH_IA_REFERENCE_AD_STYLE_GUIDE_CHIP,
  MODAL_CREATE_WITH_IA_REFERENCE_AD_DESCRIPTION,
  MODAL_CREATE_WITH_IA_REFERENCE_AD_SOURCE_LOCAL,
  MODAL_CREATE_WITH_IA_REFERENCE_AD_SOURCE_SEARCH,
  MODAL_CREATE_WITH_IA_UPLOAD_IMAGE,
  MODAL_CREATE_WITH_IA_GALLERY_BUTTON,
  MODAL_CREATE_WITH_IA_IMPORT_META_AD_LIBRARY,
} from "../../../i18n/keysTranslations";

const SOURCE_MODES = {
  LOCAL: "local",
  SEARCH: "search",
};

const getDefaultSourceMode = (showSearchSource) => {
  if (showSearchSource) {
    return SOURCE_MODES.SEARCH;
  }
  return SOURCE_MODES.LOCAL;
};

const ReferenceAdSelector = ({
  setValue,
  watch,
  onOpenGallery,
  onImportFromMetaAdLibrary = () => {},
  showLocalSources = true,
  showSearchSource = true,
}) => {
  const { t } = useTranslationApp();
  const [isUploading, setIsUploading] = useState(false);
  const [sourceMode, setSourceMode] = useState(() =>
    getDefaultSourceMode(showSearchSource)
  );
  const [localTab, setLocalTab] = useState(0);

  const dispatch = useDispatchApp();
  const { userID } = dispatch(getPathDatabaseByRole());

  const referenceAdUrl = watch("referenceAdUrl");
  const hasReference = !!referenceAdUrl;

  const sourceModeOptions = useMemo(() => {
    const options = [];
    if (showSearchSource) {
      options.push({
        value: SOURCE_MODES.SEARCH,
        label: t(MODAL_CREATE_WITH_IA_REFERENCE_AD_SOURCE_SEARCH),
      });
    }
    if (showLocalSources) {
      options.push({
        value: SOURCE_MODES.LOCAL,
        label: t(MODAL_CREATE_WITH_IA_REFERENCE_AD_SOURCE_LOCAL),
      });
    }
    return options;
  }, [showLocalSources, showSearchSource, t]);

  const onClearReferenceAd = () => {
    setValue("referenceAdUrl", "");
  };

  const onUploadComplete = (urls = []) => {
    const url = urls?.[0];
    if (url) {
      setValue("referenceAdUrl", url);
    }
  };

  const onLocalTabChange = (_event, newValue) => {
    setLocalTab(newValue);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, color: "text.primary" }}
        >
          {t(MODAL_CREATE_WITH_IA_REFERENCE_AD_TITLE)}
        </Typography>
        <Chip
          label={t(MODAL_CREATE_WITH_IA_REFERENCE_AD_STYLE_GUIDE_CHIP)}
          size="small"
          sx={{
            bgcolor: "#FFF9EB",
            color: "#B89352",
            fontWeight: 600,
            fontSize: 10,
            height: 22,
            borderRadius: 999,
            "& .MuiChip-label": {
              px: 1,
              py: 0,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            },
          }}
        />
      </Box>

      <Typography
        variant="body2"
        sx={{ color: "text.secondary", fontSize: 12, lineHeight: 1.6 }}
      >
        {t(MODAL_CREATE_WITH_IA_REFERENCE_AD_DESCRIPTION)}
      </Typography>

      {sourceModeOptions.length > 1 ? (
        <SegmentedControl
          name="referenceAdSource"
          options={sourceModeOptions}
          selectedValue={sourceMode}
          onChange={setSourceMode}
        />
      ) : null}

      {sourceMode === SOURCE_MODES.LOCAL && showLocalSources ? (
        <>
          <Tabs
            value={localTab}
            onChange={onLocalTabChange}
            sx={{
              minHeight: { xs: 36, sm: 40 },
              "& .MuiTab-root": {
                minWidth: { xs: 45, sm: 60 },
                padding: { xs: "6px 8px", sm: "6px 12px" },
              },
            }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              icon={<UploadIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />}
              aria-label={t(MODAL_CREATE_WITH_IA_UPLOAD_IMAGE)}
              sx={{
                minHeight: { xs: 36, sm: 40 },
                fontSize: { xs: 10, sm: 11 },
              }}
            />
            <Tab
              icon={<GalleryIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />}
              aria-label={t(MODAL_CREATE_WITH_IA_GALLERY_BUTTON)}
              sx={{
                minHeight: { xs: 36, sm: 40 },
                fontSize: { xs: 10, sm: 11 },
              }}
            />
          </Tabs>

          {localTab === 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <UploadFileButton
                id="upload-reference-ad"
                textUpload={MODAL_CREATE_WITH_IA_UPLOAD_IMAGE}
                colorUpload="secondary"
                onChange={onUploadComplete}
                pathStorage={getReferenceAdStoragePath(userID)}
                acceptedFormats={IMAGE_FORMATS}
                loading={isUploading}
                onCallbackLoading={(loading) => setIsUploading(loading)}
                modeUpload="sync"
                icon={<UploadIcon sx={{ fontSize: "18px !important" }} />}
                disabled={hasReference}
                sx={{
                  fontSize: FONT_SIZE_BUTTON,
                  width: "100%",
                  height: { xs: 28, sm: 30 },
                }}
              />
            </Box>
          ) : null}

          {localTab === 1 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Button
                onClick={onOpenGallery}
                color="secondary"
                size="small"
                fullWidth
                startIcon={
                  <GalleryIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                }
                disabled={hasReference}
                sx={{
                  height: { xs: 28, sm: 30 },
                  fontSize: 13,
                }}
              >
                {t(MODAL_CREATE_WITH_IA_GALLERY_BUTTON)}
              </Button>
            </Box>
          ) : null}
        </>
      ) : null}

      {sourceMode === SOURCE_MODES.SEARCH && showSearchSource ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Button
            onClick={onImportFromMetaAdLibrary}
            color="secondary"
            size="small"
            fullWidth
            startIcon={
              <MetaAdLibraryIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
            }
            disabled={hasReference}
            sx={{
              height: { xs: 28, sm: 30 },
              fontSize: 13,
            }}
          >
            {t(MODAL_CREATE_WITH_IA_IMPORT_META_AD_LIBRARY)}
          </Button>
        </Box>
      ) : null}

      {referenceAdUrl ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box
            sx={{
              position: "relative",
              width: 120,
              aspectRatio: "1",
              bgcolor: "grey.100",
              borderRadius: 1,
              border: 1,
              borderColor: "divider",
              overflow: "hidden",
            }}
          >
            <img
              src={referenceAdUrl}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            <IconButton
              size="small"
              onClick={onClearReferenceAd}
              sx={{
                position: "absolute",
                top: { xs: 2, sm: 4 },
                right: { xs: 2, sm: 4 },
                bgcolor: "black",
                width: { xs: 16, sm: 20 },
                height: { xs: 16, sm: 20 },
              }}
            >
              <CloseIcon
                sx={{
                  fontSize: { xs: 10, sm: 12 },
                  color: "white !important",
                }}
              />
            </IconButton>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default ReferenceAdSelector;
